import React from 'react';
import { Boxes, Database, Mail, MessageCircle, Receipt, Table2 } from 'lucide-react';

/**
 * IntelligenceLayerDiagram — the one genuinely abstract idea on /ai-agency-dubai, drawn
 * instead of described: the systems the client already runs, feeding a shared knowledge
 * bank, feeding a shared context layer, feeding each team's role-scoped agents.
 *
 * Built entirely from divs, borders and icon glyphs — no image file, no external request.
 * That matters here: this page is served to UAE mobile data at AED 10-50 a click, and an
 * image in this slot would compete with the LCP for no argument.
 *
 * Accessibility: every label is real text, so screen readers get the structure from the
 * DOM in top-to-bottom order, which is the same order the diagram reads. Icons and the
 * connector rules are aria-hidden.
 */

const TEAMS = ['Sales', 'Finance', 'Operations', 'HR'];

// The systems the client already runs. Generic glyphs and generic names on purpose —
// a vendor logo is a per-tool compatibility claim we'd have to defend.
const SYSTEMS = [
  { label: 'Your CRM', Icon: Database },
  { label: 'Email', Icon: Mail },
  { label: 'ERP', Icon: Boxes },
  { label: 'Accounting', Icon: Receipt },
  { label: 'WhatsApp', Icon: MessageCircle },
  { label: 'Spreadsheets', Icon: Table2 },
];

export default function IntelligenceLayerDiagram() {
  return (
    <figure className="mx-auto max-w-3xl">
      {/* Tier 1 — the tools they already run. Same card treatment as the tiers below it
          so the diagram reads as four consistent layers rather than three bars with a
          detached cluster floating above them. Top rather than bottom because the
          relationship being drawn is "feeds into", and that reads downward. */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.65)] bg-white/20 backdrop-blur-[20px] shadow-[0_20px_50px_rgba(31,38,135,0.10)] px-4 py-3.5">
        <span className="block text-center text-[11px] uppercase tracking-[0.14em] text-[rgba(20,20,43,0.55)]">
          The systems you already run
        </span>
        <ul className="mt-3 grid grid-cols-3 gap-x-2 gap-y-3 sm:grid-cols-6">
          {SYSTEMS.map(({ label, Icon }) => (
            <li key={label} className="flex flex-col items-center gap-1.5 text-center">
              <Icon className="h-5 w-5 text-[rgba(20,20,43,0.55)]" strokeWidth={1.5} aria-hidden="true" />
              <span className="text-[11px] leading-tight text-[rgba(20,20,43,0.75)]">{label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-center" aria-hidden="true">
        <span className="h-6 w-px bg-gradient-to-b from-[rgba(20,20,43,0.08)] to-[rgba(20,20,43,0.2)]" />
      </div>

      {/* Reads top-down as a flow, foundation first: what the company knows → the shared
          understanding built on it → the teams whose agents draw from it. */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.65)] bg-white/20 backdrop-blur-[20px] shadow-[0_20px_50px_rgba(31,38,135,0.10)] px-4 py-3.5 text-center">
        <span className="block text-sm font-semibold text-[#14142b]">Company knowledge bank</span>
        <span className="mt-0.5 block text-[11px] leading-tight text-[rgba(20,20,43,0.55)]">
          held once — not siloed in one team’s inbox
        </span>
      </div>

      <div className="flex justify-center" aria-hidden="true">
        <span className="h-6 w-px bg-gradient-to-b from-[rgba(20,20,43,0.08)] to-[rgba(20,20,43,0.2)]" />
      </div>

      {/* Indigo rather than coral — coral on a filled panel reads as a warning. Indigo
          is already in the page's aurora palette. */}
      <div
        className="rounded-xl px-4 py-3.5 text-center"
        style={{
          background: 'rgba(102, 126, 234, 0.14)',
          border: '1px solid rgba(129, 149, 245, 0.38)',
        }}
      >
        <span className="block text-sm font-semibold text-[#3F4FBF]">
          Shared context layer
        </span>
        <span className="mt-0.5 block text-[11px] leading-tight text-[rgba(20,20,43,0.6)]">
          your products, your customers, how you actually sell
        </span>
      </div>

      <div className="flex justify-center" aria-hidden="true">
        <span className="h-6 w-px bg-gradient-to-b from-[rgba(20,20,43,0.2)] to-[rgba(20,20,43,0.08)]" />
      </div>

      {/* The teams whose agents sit on top of both */}
      <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
        {TEAMS.map((team) => (
          <li
            key={team}
            className="rounded-xl border border-[rgba(255,255,255,0.65)] bg-white/20 backdrop-blur-[20px] shadow-[0_20px_50px_rgba(31,38,135,0.10)] px-3 py-3 text-center"
          >
            <span className="block text-sm font-semibold text-[#14142b]">{team}</span>
            <span className="mt-0.5 block text-[11px] leading-tight text-[rgba(20,20,43,0.55)]">
              agents · role access
            </span>
          </li>
        ))}
      </ul>

      <figcaption className="mt-4 text-center text-xs text-[rgba(20,20,43,0.55)]">
        Built on the systems you already run. Everything your company knows feeds one
        shared understanding — and every team’s agents work from it, which is what lets
        them hand off to each other.
      </figcaption>
    </figure>
  );
}
